import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * FeaturePrioritisationPage Component
 * 
 * Displays feature prioritisation tools content like the legacy HTML site
 */
const FeaturePrioritisationPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // CSS style for justified text
  const justifiedTextStyle = {
    textAlign: "justify"
  };

  // Define intro content from the legacy HTML
  const featurePrioritisationIntroHtml = `
    <p class="justified-text">
    From the Synthesised research, insights will have been accumulated as to what is required to be included in any solution.
    </p>
    <p class="justified-text">
    These potential features need to be prioritised before they are passed on to the ideation phase. 
    </p>
  `;

  // Templates HTML for each tool
  const featureLogTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/feature-prioritisation/feature-log.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kiGWGjc=/" className="link-table">Miro</a>
    </>
  );

  const moscowTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/feature-prioritisation/moscow.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kiGd8KU=/" className="link-table">Miro</a>
    </>
  );

  const weightedScoringTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/feature-prioritisation/weighted-scoring.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kiFmuLE=/" className="link-table">Miro</a>
    </>
  );

  const buyAFeatureTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/feature-prioritisation/buy-a-feature.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kiQ5w2o=/" className="link-table">Miro</a>
    </>
  );

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {/* Feature Prioritisation Intro */}
      <div id="section-tools-feature-prioritisation" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/synthesis">Synthesis</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/synthesis/feature-prioritisation">Feature Prioritisation</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={featurePrioritisationIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#feature-log">Feature Log</Link></div>
            <div><Link className="tool-section-cta" to="#moscow">MoSCoW</Link></div>
            <div><Link className="tool-section-cta" to="#weighted-scoring">Weighted Scoring</Link></div>
            <div><Link className="tool-section-cta" to="#buy-a-feature">Buy-a-Feature</Link></div>
          </div>
        </div>
      </div>

      {/* Feature Log Section */}
      <main className="case-study">
        <span className="case-tool-title" id="feature-log">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="feature-log">
            <span className="tool-title-number">
              <Link to="#feature-log">Feature Log</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/feature-prioritisation">Feature Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#feature-log"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-feature-log" 
            id="tab-feature-log-overview" 
            defaultChecked
          />
          <label htmlFor="tab-feature-log-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Feature Prioritisation", "18/10/20", featureLogTemplates, <a href="#">N/A</a>)}
              <div className="tool-opening">
                <p className="justified-text">
                Naturally throughout any Product Development process, ideas will flow and might take the form of a feature, or lead on to a specific feature. 
                </p>
                
                <p className="justified-text">
                Logging these feature ideas regularly will help clear minds, but also inspire solutions down the line. 
                </p>
                
                <p className="justified-text">
                By collating a feature log, it also means that previously proposed, but de-prioritised features could still be useful for future problem solving solutions. 
                </p>              
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.productplan.com/glossary/feature-backlog/" className="link-table">What Is a Feature Backlog?</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.productplan.com/learn/prioritize-product-backlog/" className="link-table">How to Prioritize Your Product Backlog</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-feature-log" 
            id="tab-feature-log-template"
          />
          <label htmlFor="tab-feature-log-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kiGWGjc=/?pres=1&frameId=3074457350698217465" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Feature Log Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* MoSCoW Section */}
      <main className="case-study">
        <span className="case-tool-title" id="moscow">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="moscow">
            <span className="tool-title-number">
              <Link to="#moscow">MoSCoW</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/feature-prioritisation">Feature Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#moscow"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-moscow" 
            id="tab-moscow-overview" 
            defaultChecked
          />
          <label htmlFor="tab-moscow-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Feature Prioritisation", "18/10/20", moscowTemplates, <a href="https://en.wikipedia.org/wiki/MoSCoW_method">Dai Clegg</a>)}
              <div className="tool-opening">
                <p className="justified-text">
                Once we have a general idea of how we are going to tackle the problem, defined as a HMW, or a Job/User Story, we can begin to prioritise what would be needed in the product.
                </p>
                
                <p className="justified-text">
                The MoSCoW method is very helpful as a lean, quick, intuitive means of prioritising these features. An acronym for Must have, Could have, Should have, Would have - it helps rank features by order of importance, but also doesn't discount any features and if there is the resource, all will be built. 
                </p>
                
                <p className="justified-text">
                Before starting this process, ensure that it is clear how much resource should be dedicated to each category, and how disputes are resolved amongst team members different needs and priorities.
                </p>
                
                <span className="underline">Must Have</span>
                <p className="justified-text">
                Non-Negotiable features that if missing, would render the product useless. A lean way of product creation when resources are right, perfect for an MVP.
                </p>    

                <span className="underline">Should Have</span>
                <p className="justified-text">
                This is where real value can be added, whilst they aren't essential for the product to function as intended, they will help significantly improve the experience for the user. 
                </p>
                
                <span className="underline">Could Have</span>
                <p className="justified-text">               
                The nice to haves - won't add significant value, but if there is the resource can still be a nice addition to level up the product. 
                </p>     
                
                <span className="underline">Would Have</span>
                <p className="justified-text">               
                If there is resource, then this feature would be added, but likely it won't be added in this sprint, but it could be used again for future prioritisation. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.productplan.com/glossary/moscow-prioritization/" className="link-table">What is MoSCoW Prioritization?</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.projectsmart.co.uk/moscow-method.php " className="link-table">The Moscow Method</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-moscow" 
            id="tab-moscow-template"
          />
          <label htmlFor="tab-moscow-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kiGd8KU=/?pres=1&frameId=3074457350698767060" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="MoSCoW Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Weighted Scoring Section */}
      <main className="case-study">
        <span className="case-tool-title" id="weighted-scoring">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="weighted-scoring">
            <span className="tool-title-number">
              <Link to="#weighted-scoring">Weighted Scoring</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/feature-prioritisation">Feature Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#weighted-scoring"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-weighted-scoring" 
            id="tab-weighted-scoring-overview" 
            defaultChecked
          />
          <label htmlFor="tab-weighted-scoring-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Feature Prioritisation", "18/10/20", weightedScoringTemplates, <a href="#">N/A</a>)}
              <div className="tool-opening">
                <p className="justified-text">
                Applying scores to features helps to crystallise focus on what's important, and what areas will be impacted most by the feature.
                </p>
                
                <p className="justified-text">
                Using Weighted Scoring helps to make this process consistent and aligns teams by allowing everyone to score and prioritising from there - removing friction and enhancing cohesion. 
                </p>
                
                <p className="justified-text">
                Combining numerical scores against user benefit vs cost will help focus the team to choose the real important features and build empathy for users and for business risk. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.productplan.com/glossary/weighted-scoring/" className="link-table">Weight Scoring Explainer</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://airfocus.com/blog/weighted-decision-matrix-prioritization/" className="link-table">Weighted Decision Matrix: Your Tool Towards Pro-Level Prioritization</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-weighted-scoring" 
            id="tab-weighted-scoring-template"
          />
          <label htmlFor="tab-weighted-scoring-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kiFmuLE=/?pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Weighted Scoring Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Buy-a-Feature Section */}
      <main className="case-study">
        <span className="case-tool-title" id="buy-a-feature">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="buy-a-feature">
            <span className="tool-title-number">
              <Link to="#buy-a-feature">Buy-a-Feature</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/feature-prioritisation">Feature Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#buy-a-feature"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-buy-a-feature" 
            id="tab-buy-a-feature-overview" 
            defaultChecked
          />
          <label htmlFor="tab-buy-a-feature-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Feature Prioritisation", "18/10/20", buyAFeatureTemplates, <a href="https://www.innovationgames.com/buy-a-feature/">Luke Hohmann</a>)}
              <div className="tool-opening">
                <p className="justified-text">
                When working with stakeholders, users and other team members to build out new products taking every opportunity to engage them, as well as learn is important.
                </p>
                
                <p className="justified-text">
                Buy-a-Feature prioritisation is a great tool for this, by offering stakeholders a say about what features are important you can ensure you are building the right things. It can also be helpful when struggling to prioritise or extract meaningful insights from previous research. 
                </p>
                
                <p className="justified-text">
                As the log is at the beginning of the process it is pre-research, and therefore will contain a lot of assumptions. 
                </p>
                
                <span className="underline">Steps</span>
                <ul className="bullet-tk">
                    <li>Provide stakeholders with a list of features and details with associated costs.</li>
                    <li>Provide them with a budget and ask them to go shopping.</li>
                    <li>Observe their choices, and ask them to explain, all the time extracting insights and information to further help the prioritisation process.</li>
                </ul>             
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.productplan.com/glossary/buy-a-feature/" className="link-table">Buy-a-Feature Explainer</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.innovationgames.com/buy-a-feature/" className="link-table">Buy a Feature Game</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="http://www.uxforthemasses.com/buy-the-feature/" className="link-table">How to play the 'Buy a feature' design game</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-buy-a-feature" 
            id="tab-buy-a-feature-template"
          />
          <label htmlFor="tab-buy-a-feature-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kiQ5w2o=/?pres=1&frameId=3074457350755598944" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Buy-a-Feature Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default FeaturePrioritisationPage; 